<script lang="ts">
	import { DeviceGroup, DeviceRPi, DeviceStatusListener, ProjectorPowerManager } from "luxedo-data"
	import { LoadingSpinner } from "misc-ui"
	import { fade, fly } from "svelte/transition"
	import { Toast } from "toaster"
	import { ProjectorPowerFallback } from "../ProjectorPowerFallback.svelte"

	interface Props {
		device: DeviceRPi | DeviceGroup
		isProjectorOn: boolean
	}

	let { device, isProjectorOn }: Props = $props()

	let deviceTitle = $derived(device instanceof DeviceGroup ? "Group" : "Device")

	let deviceStatusListener = $derived(new DeviceStatusListener(device))
	let deviceStatus: string = $derived(deviceStatusListener.status)

	const offlineStatuses: Array<typeof deviceStatus> = [
		"EMPTY",
		"DORMANT",
		"INACTIVE",
		"INIT",
		"LOST",
		"NEW",
		"OFF",
		"OFFLINE",
		"UPDATING",
	]

	$inspect(deviceStatus)

	let isOffline = $derived(offlineStatuses.includes(deviceStatus.toUpperCase()))
	let isChanging = $state(false)

	$effect(() => {
		return ProjectorPowerManager.subscribe((ctx) => {
			if (device.id in ctx) {
				isChanging = ctx[device.id].isChanging
				return
			}

			if (device instanceof DeviceGroup) {
				const childrenIds = device.getChildDevices().map((dev) => dev.id)
				for (const id of childrenIds) {
					if (id in ctx && ctx[id].isChanging) {
						isChanging = ctx[device.id].isChanging
						return
					}
				}
			}

			isChanging = false
		})
	})

	async function toggleProjectorPower(e: MouseEvent) {
		e.stopPropagation()
		try {
			await ProjectorPowerManager.setPower(device, isProjectorOn ? "OFF" : "ON")
		} catch (e) {
			Toast.action("Failed to detect projector power, allow playback anyway?", "Yes", () => {
				ProjectorPowerFallback.allowPlayAnyway = true
			})
		}
	}
</script>

<div class="not-ready-container" out:fly={{ x: "50%" }} in:fly={{ x: "50%" }}>
	{#if isOffline}
		<span class="not-ready-info"> {deviceTitle} is offline, power it on to play. </span>
	{:else if !isProjectorOn}
		<span class="not-ready-info"> {deviceTitle} is not ready. </span>
		<div class="action-container">
			{#if isChanging}
				<LoadingSpinner height="1.25em" color="var(--color-main)" />
			{:else}
				<button class="wake-up-btn outline-button small" onclick={toggleProjectorPower}>wake</button
				>
			{/if}
		</div>
	{:else}
		<span class="not-ready-info">Ready to play.</span>
	{/if}
</div>

<style>
	.not-ready-container {
		background-color: var(--color-bg);
		color: var(--color-text-light);

		border-radius: var(--br);
		width: 6rem;
		font-size: var(--text-small);
		box-sizing: content-box;
		line-height: 1em;
		min-height: 1.5rem;
		align-items: center;
		justify-content: center;
		padding: 0.5rem 1.25rem 0.5rem 1rem;
		border-top-left-radius: 99px;
		border-bottom-left-radius: 99px;
		z-index: -1;
		text-align: center;
		margin-right: -1rem;
		display: flex;
		flex-direction: column;
	}

	.action-container {
		display: flex;
		flex-direction: row;
		justify-content: center;
		margin-top: 0.25rem;
	}

	.action-container :global(.loader) {
		background-color: unset;
	}

	.action-container :global(.loader),
	.action-container button {
		height: 1.25em;
	}

	.wake-up-btn {
		padding: 0 0.5rem;
		border-width: 1px;
	}
</style>
